/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ChoiceGroup, IChoiceGroupOption, Icon, IDropdownOption, ILabelProps, ILabelStyles, Image, Label, Stack } from '@fluentui/react';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { Observer, observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { OrchestrationInfo, OrchestrationType } from 'src/clu/core/stores/CluIntentStore';
import { CluProjectStore } from 'src/clu/core/stores/CluProjectStore';
import {
    CluConnectedIntentsDto,
    dispatchAppStoresByOrchestrationType,
    getAppStoresByOrchestrationType,
    orchestrationTypeDropdownOptions
} from 'src/clu/modules/utils/orchestrationUtils';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useFormState } from 'src/core/hooks/useFormState';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { FormValidators } from 'src/core/utils/formValidators';
import { globalMessages } from 'src/core/utils/globalMessages';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { LuisAppStore } from 'src/luis/core/stores/LuisAppStore';
import styled from 'styled-components';

type IntentConnectionOptions = 'noConnection' | 'withConnection';
export type CluIntentCreationModalProps = BasicModalProps<CluConnectedIntentsDto> & {
    initialName?: string;
    enableIntentLinking?: boolean;
};

const messages = defineMessages({
    addIntent: { id: 'clu.cluEntityCreationModal.addIntent', defaultMessage: 'Add intent' },
    nameLabel: { id: 'clu.cluIntentCreationModal.nameLabel', defaultMessage: 'Intent name' },
    modalTitle: { id: 'clu.cluIntentCreationModal.modalTitle', defaultMessage: 'Add an intent' },
    projectNameLabel: { id: 'clu.cluIntentCreationModal.projectNameLabel', defaultMessage: 'Project name' },
    nameSectionLabel: { id: 'clu.cluIntentCreationModal.nameSectionLabel', defaultMessage: 'Name an intent' },
    namePlaceholder: { id: 'clu.cluIntentCreationModal.namePlaceholder', defaultMessage: 'Type intent name ...' },
    connectProjectTitle: { id: 'clu.cluIntentCreationModal.connectProjectTitle', defaultMessage: 'Connect to a project' },
    examplesHeader: { id: 'clu.cluIntentCreationModal.examplesHeader', defaultMessage: 'For example, these utterances:' },
    connectedServiceLabel: { id: 'clu.cluIntentCreationModal.connectedServiceLabel', defaultMessage: 'Connected service' },
    utteranceExample2: { id: 'clu.cluIntentCreationModal.utteranceExample2', defaultMessage: '"Fly me to Rio on the 24th"' },
    examplesFooter: { id: 'clu.cluIntentCreationModal.examplesFooter', defaultMessage: 'would fall under "BookFlight" intent.' },
    addIntentFailed: { id: 'clu.cluIntentCreationModal.addIntentFailed', defaultMessage: 'Adding intent "{ intentName }" failed' },
    utteranceExample1: { id: 'clu.cluIntentCreationModal.utteranceExample1', defaultMessage: '"Book me a flight to Rio next week"' },
    utterancesVersionLabel: { id: 'clu.cluIntentCreationModal.utterancesVersionLabel', defaultMessage: 'Version for utterances export' },
    projectNamePlaceholder: { id: 'clu.cluIntentCreationModal.projectNamePlaceholder', defaultMessage: 'Select project to connect with' },
    connectProjectOption: {
        id: 'clu.connectProjectOption.doNotConnectProject',
        defaultMessage: 'Yes, I want to connect it to an existing project'
    },
    doNotConnectProjectOption: {
        id: 'clu.cluIntentCreationModal.doNotConnectProjectOption',
        defaultMessage: 'No, I don’t want to connect to a project'
    },
    connectProjectDescription: {
        id: 'clu.cluIntentCreationModal.connectProjectDescription',
        defaultMessage: `You can choose to connect to a project or leave an intent unconnected.
        If you choose to connect to a project, the project you connect to must be created with
        the same resource used to create this orchestration project. If you choose not to connect
        an intent to another project, you must provide training examples to it.`
    },
    connectedServicePlaceholder: {
        id: 'clu.cluIntentCreationModal.connectedServicePlaceholder',
        defaultMessage: 'Select service to connect with'
    },
    utterancesVersionPlaceholder: {
        id: 'clu.cluIntentCreationModal.utterancesVersionPlaceholder',
        defaultMessage: 'Select version to connect with'
    },
    projectNameDescription: {
        id: 'clu.cluIntentCreationModal.projectNameDescription',
        defaultMessage: 'This is the name of the project in the connected service.'
    },
    modalConversationDescription: {
        id: 'clu.cluIntentCreationModal.modalConversationDescription',
        defaultMessage: `An intent represents a task or action that the user wants to perform.
        It is a purpose or goal expressed in a user’s utterance.`
    },
    modalOrchestrationDescription: {
        id: 'clu.cluIntentCreationModal.modalOrchestrationDescription',
        defaultMessage: `Orchestration directs intents to be handled by other conversational
        language understanding, question answering, or LUIS projects you've created. The
        utterances or questions of the target Language service will be used to train this intent.`
    },
    connectedServiceDescription: {
        id: 'clu.cluIntentCreationModal.connectedServiceDescription',
        defaultMessage: `This is the service that will handle the intent. The orchestration
project will take data from this service to understand and respond to this intent`
    },
    utterancesVersionDescription: {
        id: 'clu.cluIntentCreationModal.utterancesVersionDescription',
        defaultMessage: `If the connected service is Language Understanding,
you will need to select the version for exporting utterances`
    },
    duplicatedNameErrorMessage: {
        id: 'clu.cluIntentCreationModal.duplicatedNameErrorMessage',
        defaultMessage: 'This name already exists.'
    },
    conversationIntentCreationImage: {
        id: 'clu.cluIntentCreationModal.conversationIntentCreationImage',
        defaultMessage: 'Conversation intent creation image'
    },
    invalidIntentName: {
        id: 'clu.cluIntentCreationModal.invalidIntentName',
        defaultMessage: 'Invalid characters: You can only use letters (a-z, A-Z), numbers (0-9) and symbols @ _ . , ^ \\\\ [ ]'
    }
});

const flexLabelStyles = classNamesFunction<ILabelProps, ILabelStyles>()({ root: { display: 'flex', alignItems: 'flex-end' } });

const getDescribedLabel = (label: string, description: React.ReactNode, isRequired: boolean = false) => (
    <Label styles={flexLabelStyles} required={isRequired}>
        {label}
        <CalloutButton content={description} />
    </Label>
);

const theme = getScopedTheme('CluIntentCreationModal');

const { styleUlist } = getStylist('CluIntentCreationModal');
const ExampleList = styleUlist('ExampleList', () => ({ marginLeft: 50 }));

const ErrorIcon = styled(Icon)({
    marginTop: '30px',
    marginLeft: '10px',
    color: theme.errorIconColor
});

export const CluIntentCreationModal = withModalMounter(
    observer((props: CluIntentCreationModalProps) => {
        const { onSubmit, initialName = '', enableIntentLinking = true } = props;
        const { localize } = useLocalization();
        const { rootStore, dispatcher } = useGlobalState();

        const { formState, formStateDto, updateFormState } = useFormState<CluConnectedIntentsDto>({
            intentName: [
                initialName,
                FormValidators.createRequiredValidator(),
                FormValidators.createLengthValidator(LuisConstants.Clu.maxNamesCharLength),
                FormValidators.createBlacklistValidator({
                    errorMessage: messages.invalidIntentName,
                    characterList: LuisConstants.ResourceLimits.IntentsAndEntitiesForbiddenChars
                })
            ],
            serviceType: [null],
            projectId: [''],
            versionId: ['']
        });

        const tracker = useProgressTracker({ onComplete: () => onSubmit(formStateDto) });

        const [connectIntent, setConnectIntent] = React.useState<IntentConnectionOptions>('noConnection');

        const currentProject = rootStore.currentCluProjectStore;
        const isOrchestrationProject = currentProject.projectType === 'Orchestration';
        const projectsStoreHouse = getAppStoresByOrchestrationType(formStateDto.serviceType, rootStore);
        const isUtterancesVersionsDisabled = projectsStoreHouse?.findById(formStateDto.projectId)?.appType !== 'conversation';
        const filteredProjects =
            formStateDto.serviceType === 'Conversation'
                ? (projectsStoreHouse?.items as CluProjectStore[]).filter(p => p.projectType !== 'Orchestration')
                : projectsStoreHouse?.items;

        const projectsOptions = filteredProjects?.map<IDropdownOption>(project => ({ key: project.id, text: project.name }));
        const versionsOptions: IDropdownOption[] = !isUtterancesVersionsDisabled
            ? (projectsStoreHouse?.findById(formStateDto.projectId) as LuisAppStore).appVersionStoreHouse.items.map(version => ({
                  key: version.id,
                  text: version.id
              }))
            : [];

        const utteranceExamples = [localize(messages.utteranceExample1), localize(messages.utteranceExample2)];

        const duplicateErrorMessages =
            currentProject.intentStoreHouse.items?.find(n => n.name === formState.fields.intentName.value) &&
            localize(messages.duplicatedNameErrorMessage);

        const intentConnectionOptions: IChoiceGroupOption[] = [
            { key: 'noConnection', text: localize(messages.doNotConnectProjectOption) },
            {
                key: 'withConnection',
                text: localize(messages.connectProjectOption),
                onRenderLabel: (fieldProps, defaultRenderer) => (
                    <Stack tokens={{ childrenGap: 10 }}>
                        {defaultRenderer(fieldProps)}
                        {connectIntent === 'withConnection' && (
                            <Observer>
                                {() => (
                                    <Stack tokens={{ childrenGap: 10, padding: '0 0 0 26px' }}>
                                        <LuisDropdown
                                            key="connectedServiceDropdown"
                                            options={orchestrationTypeDropdownOptions}
                                            data-automation-id="connectedServiceDropdown"
                                            selectedKey={formState.fields.serviceType.value}
                                            placeholder={localize(messages.connectedServicePlaceholder)}
                                            onChange={(_, option) => handleConnectedServiceChange(option.key as OrchestrationType)}
                                            onRenderLabel={() =>
                                                getDescribedLabel(
                                                    localize(messages.connectedServiceLabel),
                                                    localize(messages.connectedServiceDescription)
                                                )
                                            }
                                        />

                                        <Stack horizontal>
                                            <LuisDropdown
                                                options={projectsOptions}
                                                key="connectedProjectDropdown"
                                                data-automation-id="connectedProjectDropdown"
                                                disabled={!formState.fields.serviceType.value}
                                                placeholder={localize(messages.projectNamePlaceholder)}
                                                selectedKey={formState.fields.projectId.value}
                                                onChange={(_, option) => handleConnectedProjectChange(option.key as string)}
                                                isLoading={projectsStoreHouse?.tracker.isRunning || projectsStoreHouse?.isLoading}
                                                onRenderLabel={() =>
                                                    getDescribedLabel(
                                                        localize(messages.projectNameLabel),
                                                        localize(messages.projectNameDescription)
                                                    )
                                                }
                                            />

                                            {formState.fields.serviceType.value === 'Luis' && !rootStore.hasPermissionToListLUISApps && (
                                                <LuisTooltip content={localize(globalMessages.cannotListLuisApps)}>
                                                    <ErrorIcon iconName="ErrorBadge" />
                                                </LuisTooltip>
                                            )}
                                        </Stack>

                                        {formState.fields.serviceType.value === 'Luis' && (
                                            <LuisDropdown
                                                options={versionsOptions}
                                                key="utterancesVersionDropdown"
                                                disabled={isUtterancesVersionsDisabled}
                                                data-automation-id="utterancesVersionDropdown"
                                                selectedKey={formState.fields.versionId.value}
                                                placeholder={localize(messages.utterancesVersionPlaceholder)}
                                                onChange={(_, option) => updateFormState({ versionId: option.key as string })}
                                                isLoading={
                                                    !isUtterancesVersionsDisabled &&
                                                    (projectsStoreHouse?.findById(formState.fields.projectId.value) as LuisAppStore)
                                                        ?.appVersionStoreHouse?.isLoading
                                                }
                                                onRenderLabel={() =>
                                                    getDescribedLabel(
                                                        localize(messages.utterancesVersionLabel),
                                                        localize(messages.utterancesVersionDescription)
                                                    )
                                                }
                                            />
                                        )}
                                    </Stack>
                                )}
                            </Observer>
                        )}
                    </Stack>
                )
            }
        ];

        const validateConnectionOptions = () => connectIntent === 'noConnection' || Boolean(formState.fields.projectId.value);

        const handleConnectedServiceChange = (service: OrchestrationType) => {
            dispatchAppStoresByOrchestrationType(service, dispatcher);
            updateFormState({ serviceType: service, projectId: null });
        };

        const handleConnectedProjectChange = (projectId: string) => {
            if (formStateDto.serviceType === 'Luis') {
                dispatcher.dispatch('loadAppVersions', { context: { appId: projectId } });
                updateFormState({
                    projectId,
                    versionId: (projectsStoreHouse.findById(projectId) as LuisAppStore).activeVersion
                });
            }

            updateFormState({ projectId });
        };

        const getOrchestrationInfo = (): OrchestrationInfo => {
            const project = projectsStoreHouse?.findById(formStateDto.projectId);

            switch (formStateDto.serviceType) {
                case 'Conversation':
                    return { orchestrationType: 'Conversation', projectName: project.name, slotName: '' };
                case 'Luis':
                    return {
                        slotName: '',
                        appId: project.id,
                        orchestrationType: 'Luis',
                        versionId: formStateDto.versionId
                    };
                case 'QuestionAnswering':
                    return { orchestrationType: 'QuestionAnswering', projectName: project.name };
                default:
            }
        };

        const handleCreateIntent = () => {
            if (!formState.form.valid) {
                return;
            }

            dispatcher.dispatch('addCluIntents', {
                tracker,
                intents: [{ name: formStateDto.intentName, orchestrationInfo: getOrchestrationInfo() }],
                toastData: { failed: { title: localize(messages.addIntentFailed, { intentName: formStateDto.intentName }) } }
            });
        };

        return (
            <BasicModal
                {...props}
                minWidth={750}
                disabled={tracker.isRunning}
                onSubmit={handleCreateIntent}
                errorMessage={tracker.error?.message}
                doneButtonText={localize(messages.addIntent)}
                dialogContentProps={{ title: localize(messages.modalTitle) }}
                valid={formState.form.valid && !duplicateErrorMessages && validateConnectionOptions()}>
                <Stack tokens={{ childrenGap: 12, padding: '0 0 19px 0' }}>
                    <LuisText>
                        {localize(isOrchestrationProject ? messages.modalOrchestrationDescription : messages.modalConversationDescription)}
                    </LuisText>

                    <Stack styles={{ root: { flexWrap: 'wrap' } }} horizontal verticalAlign="start" tokens={{ childrenGap: 30 }}>
                        <Stack grow tokens={{ childrenGap: 14 }}>
                            {!isOrchestrationProject && (
                                <Stack verticalFill tokens={{ childrenGap: 10 }}>
                                    <LuisText>{localize(messages.examplesHeader)}</LuisText>
                                    <ExampleList>
                                        {utteranceExamples.map((example, index) => (
                                            <li key={index}>{example}</li>
                                        ))}
                                    </ExampleList>
                                    <LuisText>{localize(messages.examplesFooter)}</LuisText>
                                </Stack>
                            )}

                            <LuisText fontWeight={500}>{localize(messages.nameSectionLabel)}</LuisText>

                            <Stack tokens={{ childrenGap: 9 }}>
                                <LuisTextField
                                    required
                                    data-automation-id="intentName"
                                    label={localize(messages.nameLabel)}
                                    value={formState.fields.intentName.value}
                                    placeholder={localize(messages.namePlaceholder)}
                                    onChange={(_, name) => updateFormState({ intentName: name })}
                                    styles={isOrchestrationProject && { root: { maxWidth: '50%' } }}
                                    onEnterKeyUp={() => formState.form.valid && handleCreateIntent()}
                                    errorMessage={formState.fields.intentName.errorMessage || duplicateErrorMessages}
                                />

                                {enableIntentLinking && [
                                    <LuisText key="connectProjectTitle" fontWeight={500}>
                                        {localize(messages.connectProjectTitle)}
                                    </LuisText>,
                                    <LuisText key="connectProjectDescription">{localize(messages.connectProjectDescription)}</LuisText>,
                                    <ChoiceGroup
                                        selectedKey={connectIntent}
                                        key="connectProjectChoiceGroup"
                                        options={intentConnectionOptions}
                                        onChange={(_, opt) => setConnectIntent(opt.key as IntentConnectionOptions)}
                                    />
                                ]}
                            </Stack>
                        </Stack>

                        {!isOrchestrationProject && (
                            <Image
                                styles={{ image: { width: '100%', height: 'auto' } }}
                                alt={localize(messages.conversationIntentCreationImage)}
                                src={getAssetsUrl('assets/images/clu/conversationIntentCreation.svg')}
                            />
                        )}
                    </Stack>
                </Stack>
            </BasicModal>
        );
    })
);
